/*
 * BeStack / BeRow and their spacers. Thin by design -- a class name and at most
 * one custom property. Behaviour and rationale: layout/stack.css, Notes/69.
 */

import type { CSSProperties, ElementType, HTMLAttributes, ReactNode } from "react";
import { type BeCommon, beComponent } from "./create.tsx";

/**
 * THE spacing vocabulary: one step per token in tokens/spacing.css, and nothing
 * else. A layout number is a count of something NAMED, never a measurement.
 *
 * `row` was missing here, and the old `BeGap = BeSpace | (string & {})` escape
 * hatch is what hid it: `gap="row"` type-checked, fell through `space()`
 * unrecognised, and emitted `--be-gap: row` -- invalid, so the container's gap
 * computed to `normal` and collapsed. One live site (TooltipDemo) had been
 * running with NO gap. An arbitrary-length hatch does not just permit off-scale
 * values, it converts a typo into silent wrong output.
 */
export type BeSpace =
	/** **0** — the parts MEET: a shell's panes, a card whose header draws its own rule. */
	| "none"
	/* ---- STRUCTURAL: spaces the parts of ONE control. Never varies. -------- */
	/** **2px** — a hairline. The `fancy` border width, agreed by ArpLayout, Haiku
	 * `BScrollView` and `BSeparatorView` (Notes/65 §3.6). */
	| "hair"
	/** **3px** — the lead a control keeps inside its band. liblayout's panel
	 * padding (`M_RAISED_BORDER,3`), and R5's own `TOP_MARGIN`: a 13px check box
	 * centred in the 19px control band lands at exactly 3. */
	| "tight"
	/** **5px** — a flush inset, from the window resize knob's offset
	 * (`window.css`). Also `radio.css`'s R5-sourced 5. */
	| "inset"
	/* ---- RHYTHMIC: spaces SEPARATE things. The only steps a density or -----
	     theme layer would ever be allowed to re-point (GOV.UK model, Notes/73). */
	/** **8px** — one thing after another; the DEFAULT gap. `.be-window__content`
	 * padding, and BeMail's leading inset. */
	| "item"
	/** **11px** — a label to the thing it labels. BeMail and People both place
	 * every field at `offset − StringWidth(label) − 11` (Notes/66 §2). */
	| "label"
	/** **14px** — related controls as a cluster. `B_V_SCROLL_BAR_WIDTH`, which is
	 * also `--be-scrollbar-size`. */
	| "group"
	/** **20px** — one region of a window from the next. liblayout's single fixed
	 * strut, `Space(minimax(20, 0, 20, 0))`. */
	| "section"
	/** **25px** — the form row pitch: a 16px field plus a 9px gap. The one number
	 * BeMail and People agree on literally (Notes/66 §2). */
	| "row";

/* Anything that can be a flex child takes this, so the word is declared once and
 * cannot come to read differently on two components. */
export type BeGrowable = {
	/** Take the leftover main-axis space, from the preferred size rather than zero.
	 * At most one per row. */
	grow?: boolean;
};

/* Polymorphic by design (`as="ol"`, `as="li"`), so the DOM attributes come from
 * the generic HTMLAttributes rather than one element's type -- see create.tsx on
 * why `as` lives on the layout primitives and not on the controls. */
export type BeStackProps = BeCommon &
	BeGrowable &
	Omit<HTMLAttributes<HTMLElement>, "className" | "style"> & {
		children?: ReactNode;
		gap?: BeSpace;
		/** Inset on all sides. The CONTAINER owns padding, so no child carries one. */
		pad?: BeSpace;
		/** Fill the parent rather than the content. Does nothing if the parent is unsized. */
		fill?: boolean;
		align?: "start" | "center" | "end" | "stretch";
		as?: ElementType;
	};

export type BeRowProps = Omit<BeStackProps, "align"> & {
	/** "baseline" aligns the children's TEXT, not their boxes -- for a band that
	 * mixes controls of different heights (field + check box + dropdown). */
	align?: "start" | "center" | "end" | "stretch" | "baseline";
	/**
	 * A form row: establishes the R5 control band (--be-control-h) and centres
	 * its children in it, so controls of different heights line up without any
	 * of them changing size. A 13px check box lands at R5's own 3px lead.
	 * Composite controls (slider) do not belong in one -- Notes/71.
	 */
	band?: boolean;
	justify?: "start" | "center" | "end" | "between";
	wrap?: boolean;
};

/** A scale step becomes its token reference. Total, because BeSpace is closed. */
export const space = (v: BeSpace): string => `var(--be-space-${v})`;

const boxStyle = (gap?: BeSpace, pad?: BeSpace, style?: CSSProperties): CSSProperties | undefined => {
	if (!gap && !pad) return style;
	return {
		...style,
		...(gap ? { "--be-gap": space(gap) } : null),
		...(pad ? { "--be-pad": space(pad) } : null),
	} as CSSProperties;
};

export const BeStack = beComponent<BeStackProps, HTMLElement>("BeStack", (props, be) => {
	const { children, gap, pad, align, fill, grow, as: Tag = "div", scale, className, style, ...rest } = props;
	// every explicit align emits its class -- a stack's default is `stretch`, so
	// dropping "start" as "the default" silently ignored it (see stack.css)
	const root = be("be-stack", fill && "is-fill", grow && "be-grow", align && `is-align-${align}`);
	return (
		<Tag {...rest} {...root} style={boxStyle(gap, pad, root.style)}>
			{children}
		</Tag>
	);
});

export const BeRow = beComponent<BeRowProps, HTMLElement>("BeRow", (props, be) => {
	const { children, gap, pad, align, band, justify, wrap, fill, grow, as: Tag = "div", scale, className, style, ...rest } = props;
	const root = be(
		"be-row",
		fill && "is-fill",
		grow && "be-grow",
		band && "is-band",
		// the band centres; an explicit align still wins
		align && `is-align-${align}`,
		justify && justify !== "start" && `is-justify-${justify}`,
		wrap && "is-wrap",
	);
	return (
		<Tag {...rest} {...root} style={boxStyle(gap, pad, root.style)}>
			{children}
		</Tag>
	);
});

export function BeGlue() {
	return <div className="be-glue" aria-hidden="true" />;
}

export function BeStrut({ size }: { size: number }) {
	return <div className="be-strut" aria-hidden="true" style={{ "--be-strut": `calc(${size} * var(--be-px))` } as CSSProperties} />;
}

/** Takes the leftover space in a row. At most one per row -- see stack.css. */
export function BeGrow({ children }: { children?: ReactNode }) {
	return <div className="be-grow">{children}</div>;
}

/** Yields when the row runs out of room, rather than overflowing it. */
export function BeShrink({ children }: { children?: ReactNode }) {
	return <div className="be-shrink">{children}</div>;
}
